import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import UpperCaseInput from '../../../components/UpperCaseInput';
import { Label } from '../../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Plus, Pencil, Trash2, UserCircle, Phone, Upload, Users, Search } from 'lucide-react';
import { useAuth } from '../../../App';
import { API_URL, INITIAL_DRIVER_FORM } from '../utils/constants';

const DriversTab = ({ drivers, companies, onRefresh, onBulkUpload, onAssign, getCompanyName, isTransportista }) => {
  const { user: currentUser } = useAuth();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [formData, setFormData] = useState(INITIAL_DRIVER_FORM);
  const [justification, setJustification] = useState('');
  
  // Filtros
  const [filterName, setFilterName] = useState('');
  const [filterDni, setFilterDni] = useState('');
  const [filterCompany, setFilterCompany] = useState('');
  
  // Filtrar choferes
  const filteredDrivers = useMemo(() => {
    return drivers.filter(d => {
      if (filterName && !d.name?.toLowerCase().includes(filterName.toLowerCase())) return false;
      if (filterDni && !d.dni?.toLowerCase().includes(filterDni.toLowerCase())) return false;
      if (filterCompany && d.company_id !== filterCompany) return false;
      return true;
    });
  }, [drivers, filterName, filterDni, filterCompany]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/drivers`, formData);
      toast.success('Chofer creado exitosamente');
      setIsCreateModalOpen(false);
      resetForm();
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear chofer');
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!justification.trim()) {
      toast.error('La justificación es obligatoria');
      return;
    }
    try {
      await axios.put(`${API_URL}/drivers/${selectedDriver.id}`, { justification, data: formData });
      toast.success('Chofer actualizado exitosamente');
      setIsEditModalOpen(false);
      resetForm();
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar chofer');
    }
  };

  const handleDelete = async (driverId) => {
    if (!window.confirm('¿Estás seguro de eliminar este chofer?')) return;
    try {
      await axios.delete(`${API_URL}/drivers/${driverId}`);
      toast.success('Chofer eliminado');
      onRefresh();
    } catch (error) {
      toast.error('Error al eliminar chofer');
    }
  };

  const resetForm = () => {
    setFormData(INITIAL_DRIVER_FORM);
    setJustification('');
    setSelectedDriver(null);
  };

  const openEditModal = (driver) => {
    setSelectedDriver(driver);
    setFormData({ name: driver.name, dni: driver.dni, cuit_cuil: driver.cuit_cuil, phone: driver.phone, company_id: driver.company_id });
    setIsEditModalOpen(true);
  };

  return (
    <div className="space-y-6" data-testid="drivers-tab">
      <div className="flex items-center justify-between">
        <p className="text-slate-600">Gestión de choferes</p>
        <div className="flex gap-2">
          {currentUser.role === 'MASTER' && (
            <>
              <Button onClick={() => onBulkUpload('drivers')} variant="outline" size="sm" className="text-emerald-600 border-emerald-300 hover:bg-emerald-50">
                <Upload size={16} className="mr-1" /> Carga Masiva
              </Button>
              <Button onClick={() => onAssign('drivers')} variant="outline" size="sm" className="text-violet-600 border-violet-300 hover:bg-violet-50">
                <Users size={16} className="mr-1" /> Asignar Admin
              </Button>
            </>
          )}
          <Button onClick={() => setIsCreateModalOpen(true)} className="bg-primary hover:bg-primary-hover text-white rounded-sm">
            <Plus className="mr-2" size={18} />Crear Chofer
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-slate-50 border border-slate-200 rounded-sm p-3">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Search size={16} className="text-slate-400" />
            <span className="text-sm text-slate-600">Filtrar:</span>
          </div>
          <Input
            placeholder="Buscar por nombre..."
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
            className="h-8 text-sm w-40"
          />
          <Input
            placeholder="Buscar por DNI..."
            value={filterDni}
            onChange={(e) => setFilterDni(e.target.value)}
            className="h-8 text-sm w-32"
          />
          <Select value={filterCompany || "all"} onValueChange={(v) => setFilterCompany(v === "all" ? "" : v)}>
            <SelectTrigger className="w-48 h-8 text-sm">
              <SelectValue placeholder="Todas las empresas" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las empresas</SelectItem>
              {companies.map(c => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
            </SelectContent>
          </Select>
          {(filterName || filterDni || filterCompany) && (
            <Button variant="ghost" size="sm" onClick={() => { setFilterName(''); setFilterDni(''); setFilterCompany(''); }} className="text-slate-500 h-8">
              Limpiar
            </Button>
          )}
          <span className="text-xs text-slate-500">{filteredDrivers.length} de {drivers.length}</span>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Nombre</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">DNI</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">CUIT/CUIL</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Teléfono</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Empresa</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {filteredDrivers.map((driver) => (
              <tr key={driver.id} className="hover:bg-slate-50 transition-colors">
                <td className="px-6 py-3">
                  <div className="flex items-center gap-2">
                    <UserCircle size={16} className="text-slate-400" />
                    <span className="text-sm font-medium text-slate-900">{driver.name}</span>
                  </div>
                </td>
                <td className="px-6 py-3 text-sm font-mono text-slate-900">{driver.dni}</td>
                <td className="px-6 py-3 text-sm font-mono text-slate-600">{driver.cuit_cuil}</td>
                <td className="px-6 py-3">
                  <div className="flex items-center gap-2">
                    <Phone size={14} className="text-slate-400" />
                    <span className="text-sm text-slate-600">{driver.phone}</span>
                  </div>
                </td>
                <td className="px-6 py-3 text-sm text-slate-600">{getCompanyName(driver.company_id)}</td>
                <td className="px-6 py-3">
                  <div className="flex items-center gap-2">
                    <Button onClick={() => openEditModal(driver)} variant="ghost" size="sm" className="text-blue-600 hover:bg-blue-50"><Pencil size={16} /></Button>
                    <Button onClick={() => handleDelete(driver.id)} variant="ghost" size="sm" className="text-red-600 hover:bg-red-50"><Trash2 size={16} /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create Modal */}
      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Crear Chofer</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2"><Label>Nombre *</Label><UpperCaseInput value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required /></div>
            <div className="space-y-2"><Label>DNI *</Label><UpperCaseInput value={formData.dni} onChange={(e) => setFormData({ ...formData, dni: e.target.value })} required /></div>
            <div className="space-y-2"><Label>CUIT/CUIL *</Label><UpperCaseInput value={formData.cuit_cuil} onChange={(e) => setFormData({ ...formData, cuit_cuil: e.target.value })} required /></div>
            <div className="space-y-2"><Label>Teléfono *</Label><UpperCaseInput value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} required /></div>
            <div className="space-y-2">
              <Label>Empresa *</Label>
              <Select value={formData.company_id} onValueChange={(value) => setFormData({ ...formData, company_id: value })}>
                <SelectTrigger><SelectValue placeholder="Seleccionar empresa" /></SelectTrigger>
                <SelectContent>{companies.map(c => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <Button type="submit" className="w-full bg-primary hover:bg-primary-hover text-white">Crear Chofer</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Editar Chofer</DialogTitle></DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4">
            <div className="space-y-2"><Label>Nombre</Label><UpperCaseInput value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} /></div>
            <div className="space-y-2"><Label>DNI</Label><UpperCaseInput value={formData.dni} onChange={(e) => setFormData({ ...formData, dni: e.target.value })} /></div>
            <div className="space-y-2"><Label>CUIT/CUIL</Label><UpperCaseInput value={formData.cuit_cuil} onChange={(e) => setFormData({ ...formData, cuit_cuil: e.target.value })} /></div>
            <div className="space-y-2"><Label>Teléfono</Label><UpperCaseInput value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} /></div>
            <div className="space-y-2">
              <Label>Empresa Transportista</Label>
              <Select value={formData.company_id} onValueChange={(value) => setFormData({ ...formData, company_id: value })} data-testid="edit-driver-company-select">
                <SelectTrigger className="w-full"><SelectValue placeholder="Seleccionar empresa" /></SelectTrigger>
                <SelectContent>{companies.map(c => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-red-600">Justificación (obligatoria) *</Label>
              <UpperCaseInput value={justification} onChange={(e) => setJustification(e.target.value)} required placeholder="Motivo de la edición" />
            </div>
            <Button type="submit" className="w-full bg-primary hover:bg-primary-hover text-white">Actualizar Chofer</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DriversTab;
